import { create } from "zustand";
import {
  DEFAULT_LAB_ORDER,
  type HaneyFrequency,
  type LabOrderConfig,
} from "../lib/labTests";
import { defaultPackageFor, packagesFor } from "../lib/soilLabs";

interface LabOrderState extends LabOrderConfig {
  setPanel: (panel: string) => void;
  setHaney: (patch: Partial<HaneyFrequency>) => void;
  /* * the order options (depth, lab, package, cores, line length, phone) — one patch setter */
  setOptions: (
    patch: Partial<
      Pick<
        LabOrderConfig,
        | "depthIn"
        | "lab"
        | "labPackage"
        | "coresPerSample"
        | "coreLineFt"
        | "notifyPhone"
      >
    >,
  ) => void;
  reset: () => void;
  snapshot: () => LabOrderConfig;
}

const pick = (s: LabOrderConfig): LabOrderConfig => ({
  panel: s.panel,
  haney: { ...s.haney },
  depthIn: s.depthIn,
  lab: s.lab,
  labPackage: s.labPackage,
  coresPerSample: s.coresPerSample,
  coreLineFt: s.coreLineFt,
  notifyPhone: s.notifyPhone,
});

export const useLabOrder = create<LabOrderState>((set, get) => ({
  ...DEFAULT_LAB_ORDER,
  setPanel: (panel) => set({ panel }),
  setHaney: (patch) => set((s) => ({ haney: { ...s.haney, ...patch } })),
  setOptions: (patch) =>
    set((s) => {
      const next = { ...patch };
      // switching LAB re-picks the package: a package code belongs to one lab's menu, and a stale one
      // from the previous lab would put a package on the order the chosen lab does not offer
      if (
        patch.lab !== undefined &&
        patch.lab !== s.lab &&
        patch.labPackage === undefined
      ) {
        const keep = packagesFor(patch.lab).some(
          (p) => p.code === s.labPackage,
        );
        next.labPackage = keep ? s.labPackage : defaultPackageFor(patch.lab);
      }
      return next;
    }),
  reset: () => set(pick(DEFAULT_LAB_ORDER)),
  snapshot: () => pick(get()),
}));
